import React, { FunctionComponent, ReactElement, ReactNode } from 'react';
import { connect } from 'react-redux';
import { useDisplayLanguage, useTargetLanguage } from '../../components/LanguageContext';
import { TextTranslation } from '../../constants/schemas/TextTranslation';
import { findCustomOrCommonText, makeFindCustomOrCommonTextTranslation } from '../../models/entities/selectors';
import { AppState } from '../../store';
import { EntityFields, EntityStringId, IEntity, IEntityType } from 'app/types/entity';
import { Language } from 'app/constants/enums/Language';
import { useShowLessonTranslations } from 'app/containers/publishedLesson/LessonTranslationToggle';

export enum Fallback {
  Hide,
  // Indicates that the text field should be used for looking up the translation.
  TargetLanguage,
}

type Children = (props: IArgProps) => ReactNode;

interface IProps<T extends IEntity> {
  children: Children;
  fallback?: Fallback;
  field: keyof EntityFields<T>;
  id: EntityStringId;
  type: IEntityType<T>;
}

interface IStateProps extends Omit<IProps<any>, 'children'> {
  displayLanguage: Language;
  targetLanguage: Language;
  showTranslations: boolean;
}

type EntityTranslationRendererProps = IArgProps & {
  children: Children;
};

interface IArgProps {
  /**
   * Language of the translation or the text in case of fallback
   */
  language?: Language;
  /**
   * Text of the translation or the original text in case of
   * a fallback
   */
  translation: string;
}

export const makeGetEntityTranslation = () => {
  const findCustomOrCommonTextTranslation = makeFindCustomOrCommonTextTranslation();

  return (state: AppState, props: IStateProps): IArgProps => {
    const { displayLanguage, fallback, targetLanguage } = props;

    const textTranslation: TextTranslation | undefined = findCustomOrCommonTextTranslation(state, props);

    // If the translation exists, use that.
    if (textTranslation) {
      return {
        translation: textTranslation.translation,
        language:    displayLanguage,
      };
    }

    // Otherwise, decide based on the fallback.
    if (fallback === Fallback.TargetLanguage) {
      const text = findCustomOrCommonText(state, props);

      if (text) {
        // Use the contextual target language to determine what the actual language of the translation is.
        return {
          translation: text.text,
          language:    targetLanguage,
        };
      }
    }

    return { translation: '' };
  };
};

const EntityTranslationRenderer: FunctionComponent<EntityTranslationRendererProps> = ({ children, ...rest }) => {
  return children(rest) as ReactElement<any>;
};

const Connected = connect(
  makeGetEntityTranslation,
  {},
  // Don't pass ownProps, only state props and children
  (stateProps, _, { children }: IStateProps & { children: Children }) => ({ ...stateProps, children }),
)(EntityTranslationRenderer);

/**
 * Fetches the appropriate translation field for an entity text field. The displayLanguage will
 * default to the language display language.
 */
function EntityTranslation<T extends IEntity>(props: IProps<T>) {
  const displayLanguage = useDisplayLanguage();
  const targetLanguage = useTargetLanguage();
  const showTranslations = useShowLessonTranslations();

  return (
    <Connected
      targetLanguage={targetLanguage}
      displayLanguage={displayLanguage}
      showTranslations={showTranslations}
      {...props}
    />
  );
}

export default EntityTranslation;
